// Empty + searching states for the find-references workspace tab.
//
// The headline stays terse; the detail line explains WHAT was searched so a
// zero-result answer is trustworthy instead of ambiguous. The copy is derived
// from the real index scope, not from what the feature "feels like" it covers:
//
//   • `indexBuilder.buildIndexForConnection` walks `listParseableObjects`
//     (view / inline-tvf / scalar-function / default-constraint /
//     check-constraint) + `listProceduralObjects` (procedure / trigger).
//   • The index is built per (connection, database) — for the database the
//     user asked about — so the detail line names that database; objects in
//     other databases are never enumerated. EXCEPT on MySQL and Oracle, whose
//     catalog listings enumerate every schema the login can see (a MySQL
//     "database" is a schema; Oracle's ALL_* views span owners) — there the
//     index, and the wording, cover the whole server.
//   • This TAB lists stored objects only — the text in the open editor is not
//     part of its result set. (Monaco's own Peek References does include the
//     buffer's occurrences, via `referenceProvider.provideReferences`; the two
//     surfaces answer different questions.)
//   • SQLite's fetcher lists views + CREATE TABLE text (for inline DEFAULT /
//     CHECK constraints) and nothing else — no stored routines, and its
//     procedural feed returns `[]`, so triggers are not indexed either.
//   • `mock` / `kusto` return `[]` from both feeds — nothing is indexable.
//
// The searching state covers the window between opening the tab and the
// database's index being built (the build is deferred / on demand — see
// `connection-lifecycle/navigationalLifecycle.ts`), so the user never reads a
// not-yet-built index as "no references".

import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import type { DatabaseEngineId } from '@/core/engines';

import type { IdentifierKind, QualifiedName } from '../types';

type ReferencesEmptyStateProps = {
  readonly identifier: QualifiedName;
  readonly identifierKind: IdentifierKind;
  readonly engineId: DatabaseEngineId;
  /** The database whose objects were indexed — `''` when the connection has
   *  no configured database and the login default was searched. */
  readonly database?: string;
};

export function ReferencesEmptyState({
  identifier,
  identifierKind,
  engineId,
  database,
}: ReferencesEmptyStateProps): React.ReactElement {
  return (
    <div
      className="text-sm text-muted-foreground py-8 text-center"
      data-testid="references-empty"
    >
      <p className="font-medium">
        No references found for {formatIdentifier(identifier)}.
      </p>
      <p
        className="text-xs mt-1.5 mx-auto max-w-md text-pretty"
        data-testid="references-empty-detail"
      >
        {emptyStateDetail(identifierKind, engineId, database)}
      </p>
    </div>
  );
}

type ReferencesErrorStateProps = {
  /** Why the index is unavailable, as the opener phrased it. */
  readonly message?: string;
};

/** Shown when the target database's index could not be built while the
 *  connection is still live. Distinct from the empty state on purpose: "no
 *  references" and "nothing was searched" mean opposite things, and reporting
 *  the second as the first is the false negative this surface exists to
 *  avoid. */
export function ReferencesErrorState({ message }: ReferencesErrorStateProps): React.ReactElement {
  return (
    <div
      className="text-sm text-muted-foreground py-8 text-center"
      data-testid="references-error"
      role="status"
    >
      <p className="font-medium inline-flex items-center gap-2 text-foreground">
        <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
        {message ?? 'The index for this database could not be built — try again.'}
      </p>
      <p className="text-xs mt-1.5 mx-auto max-w-md text-pretty">
        Nothing was searched, so this is not an empty result. Run the search again — the index
        is built on the first request and reused afterwards.
      </p>
    </div>
  );
}

type ReferencesSearchingStateProps = {
  readonly identifier: QualifiedName;
  readonly engineId: DatabaseEngineId;
  readonly database?: string;
};

/** Shown while the target database's identifier index is being built. */
export function ReferencesSearchingState({
  identifier,
  engineId,
  database,
}: ReferencesSearchingStateProps): React.ReactElement {
  return (
    <div
      className="text-sm text-muted-foreground py-8 text-center"
      data-testid="references-loading"
      role="status"
    >
      <p className="font-medium inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Searching for references to {formatIdentifier(identifier)}…
      </p>
      <p className="text-xs mt-1.5 mx-auto max-w-md text-pretty">
        Reading the stored object definitions {searchScope(engineId, database)}. The first search
        on a database indexes it; later searches reuse the index.
      </p>
    </div>
  );
}

/** The searched object kinds, phrased for the engine actually in play.
 *  `null` = this connection type exposes no indexable stored objects. */
function searchedObjectPhrase(engineId: DatabaseEngineId): string | null {
  if (engineId === 'sqlite') return 'view or table constraint';
  if (engineId === 'mock' || engineId === 'kusto') return null;
  return 'view, function, procedure, trigger or constraint';
}

/** Noun for the thing the user searched — mirrors the header's wording so the
 *  two lines read as one thought. */
function identifierNoun(kind: IdentifierKind): string {
  switch (kind) {
    case 'column':
      return 'column';
    case 'table':
      return 'table';
    case 'view':
      return 'view';
    case 'routine':
      return 'routine';
  }
}

/** True for the engines whose catalog listing (and therefore the index)
 *  spans every schema the login can see rather than one database. */
function indexSpansServer(engineId: DatabaseEngineId): boolean {
  return engineId === 'mysql' || engineId === 'oracle';
}

/** "in the MandTob database" / "in this database" (no configured database:
 *  the login default was searched) / "on this server" (MySQL, Oracle). */
function searchScope(engineId: DatabaseEngineId, database: string | undefined): string {
  if (indexSpansServer(engineId)) return 'on this server';
  return database ? `in the ${database} database` : 'in this database';
}

function emptyStateDetail(
  kind: IdentifierKind,
  engineId: DatabaseEngineId,
  database: string | undefined,
): string {
  const phrase = searchedObjectPhrase(engineId);
  if (phrase === null) {
    return `This connection type doesn't expose stored SQL objects, so there is nothing to search for references in.`;
  }
  const unsearched = indexSpansServer(engineId)
    ? "the open editor isn't searched"
    : "the open editor and other databases aren't searched";
  return `No ${phrase} stored ${searchScope(engineId, database)} references this ${identifierNoun(kind)} — ${unsearched}.`;
}

function formatIdentifier(name: QualifiedName): string {
  if (name.schema && name.name) return `${name.schema}.${name.name}`;
  return name.name;
}
